/**
 * AdminBridgePanel — Global bridge health sidebar shown on all admin pages.
 * Fixed w-64, always visible. Polls every 20s. Dead bridges sort to top.
 * Supports search filtering by bridge name or client code.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { UserProfile } from "@clerk/clerk-react";
import { useClerk, useUser } from "@clerk/react-router";
import { ChevronDown, LogOut01, RefreshCw01, SearchLg, Server01, User01, XClose } from "@untitledui/icons";
import { Button as AriaButton } from "react-aria-components";
import { useNavigate } from "react-router-dom";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Avatar } from "@/components/base/avatar/avatar";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { useAdminApi } from "@/services/api/admin-api";
import type { GlobalBridgeRegistrationAdminDto } from "@/types/api/admin";
import { cx } from "@/utils/cx";

// =============================================================================
// CONSTANTS
// =============================================================================

const POLL_INTERVAL_MS = 20_000;
// Bridge sends a heartbeat every 15s. Allow 25s before marking dead.
const DEAD_THRESHOLD_MS = 25_000;

// =============================================================================
// HELPERS
// =============================================================================

function isAlive(lastHeartbeatAt: string | null): boolean {
    if (!lastHeartbeatAt) return false;
    return Date.now() - new Date(lastHeartbeatAt).getTime() < DEAD_THRESHOLD_MS;
}

function sortBridges(bridges: GlobalBridgeRegistrationAdminDto[]): GlobalBridgeRegistrationAdminDto[] {
    return [...bridges].sort((a, b) => {
        const aAlive = isAlive(a.lastHeartbeatAt);
        const bAlive = isAlive(b.lastHeartbeatAt);
        // Dead first
        if (aAlive !== bAlive) return aAlive ? 1 : -1;
        const codeCompare = a.clientCode.localeCompare(b.clientCode);
        return codeCompare !== 0 ? codeCompare : a.name.localeCompare(b.name);
    });
}

function formatRefreshedAt(date: Date | null): string {
    if (!date) return "";
    return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

// =============================================================================
// SKELETON
// =============================================================================

function BridgePanelSkeleton() {
    return (
        <div className="flex flex-col gap-1 px-2 py-2">
            {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-11 animate-pulse rounded-md bg-secondary" />
            ))}
        </div>
    );
}

// =============================================================================
// BRIDGE ROW
// =============================================================================

interface BridgeRowProps {
    bridge: GlobalBridgeRegistrationAdminDto;
    onClick: () => void;
}

function BridgeRow({ bridge, onClick }: BridgeRowProps) {
    const alive = isAlive(bridge.lastHeartbeatAt);

    return (
        <button
            onClick={onClick}
            className="flex w-full cursor-pointer items-start gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
            {/* Status dot */}
            <span className={cx("mt-1 size-2 shrink-0 rounded-full", alive ? "bg-success-500" : "bg-error-500")} />
            <div className="min-w-0 flex-1">
                {/* Bridge name */}
                <p className="truncate text-xs leading-tight font-medium text-primary">{bridge.name}</p>
                {/* Client code */}
                <p className="truncate text-xs text-tertiary">{bridge.clientCode}</p>
            </div>
            {/* Environment badge */}
            <Badge color={bridge.environment === "Production" ? "brand" : "gray"} size="sm" type="pill-color">
                {bridge.environment === "Production" ? "Prod" : "Test"}
            </Badge>
        </button>
    );
}

// =============================================================================
// ADMIN BRIDGE PANEL
// =============================================================================

export function AdminBridgePanel() {
    const adminApi = useAdminApi();
    const navigate = useNavigate();
    const { signOut } = useClerk();
    const { user } = useUser();
    const userName = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || "User" : "User";
    const userInitials = user ? `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || "U" : "U";

    const [bridges, setBridges] = useState<GlobalBridgeRegistrationAdminDto[]>([]);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [search, setSearch] = useState("");
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);

    const fetchRef = useRef<(() => Promise<void>) | null>(null);

    const fetchBridges = useCallback(async () => {
        setIsRefreshing(true);
        try {
            const result = await adminApi.listAllBridges();
            setBridges(result.data);
            setRefreshedAt(new Date());
        } catch {
            // Silent — panel is non-critical; errors don't need to surface here
        } finally {
            setIsRefreshing(false);
            setIsFirstLoad(false);
        }
    }, [adminApi]);

    useEffect(() => {
        fetchRef.current = fetchBridges;
    }, [fetchBridges]);

    useEffect(() => {
        // Initial fetch
        fetchRef.current?.();

        const interval = setInterval(() => {
            fetchRef.current?.();
        }, POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, []);

    const handleBridgeClick = useCallback(
        (bridge: GlobalBridgeRegistrationAdminDto) => {
            navigate(`/admin/clients/${bridge.clientId}?tab=bridge`);
        },
        [navigate],
    );

    const handleLogout = useCallback(async () => {
        if (isLoggingOut) return;
        setIsLoggingOut(true);
        try {
            await signOut();
            navigate("/login", { replace: true });
        } catch (error) {
            console.error("Logout failed:", error);
            setIsLoggingOut(false);
        }
    }, [isLoggingOut, signOut, navigate]);

    const sorted = sortBridges(bridges);
    const filtered = search.trim()
        ? sorted.filter((b) => {
              const q = search.trim().toLowerCase();
              return b.name.toLowerCase().includes(q) || b.clientCode.toLowerCase().includes(q);
          })
        : sorted;

    return (
        <>
            <aside className="flex h-full w-64 shrink-0 flex-col border-r border-secondary bg-primary">
                {/* User header */}
                <div className="border-b border-secondary px-3 py-3">
                    <Dropdown.Root>
                        <AriaButton className="flex cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1 outline-hidden transition duration-100 ease-linear hover:bg-secondary">
                            <Avatar size="xs" initials={userInitials} />
                            <span className="text-sm font-semibold text-primary">{userName}</span>
                            <ChevronDown className="size-3.5 text-fg-quaternary" />
                        </AriaButton>
                        <Dropdown.Popover>
                            <Dropdown.Menu>
                                <Dropdown.Item icon={User01} label="Profile" onAction={() => setShowProfileModal(true)} />
                                <Dropdown.Separator />
                                <Dropdown.Item icon={LogOut01} label="Log out" onAction={handleLogout} />
                            </Dropdown.Menu>
                        </Dropdown.Popover>
                    </Dropdown.Root>
                </div>

                {/* Bridges header */}
                <div className="flex items-center justify-between border-b border-secondary px-3 py-3">
                    <div className="flex items-center gap-1.5">
                        <Server01 className="size-4 text-tertiary" />
                        <span className="text-sm font-semibold text-primary">Bridges</span>
                    </div>
                    <button
                        onClick={() => fetchRef.current?.()}
                        disabled={isRefreshing}
                        className="rounded-md p-1 text-tertiary transition-colors hover:bg-secondary hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:pointer-events-none"
                    >
                        <RefreshCw01 className={cx("size-3.5", isRefreshing && !isFirstLoad && "animate-spin")} />
                    </button>
                </div>

                {/* Search */}
                <div className="border-b border-secondary px-2 py-2">
                    <div className="flex items-center gap-1.5 rounded-md bg-secondary px-2 py-1.5">
                        <SearchLg className="size-3.5 shrink-0 text-tertiary" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search bridges..."
                            className="min-w-0 flex-1 bg-transparent text-xs text-primary placeholder-tertiary outline-none"
                        />
                    </div>
                </div>

                {/* Refreshed at */}
                {refreshedAt && (
                    <div className="px-3 py-1">
                        <p className="text-xs text-tertiary">Updated {formatRefreshedAt(refreshedAt)}</p>
                    </div>
                )}

                {/* Bridge list */}
                <div className="flex-1 overflow-y-auto">
                    {isFirstLoad ? (
                        <BridgePanelSkeleton />
                    ) : filtered.length === 0 ? (
                        <p className="px-3 py-4 text-xs text-tertiary">{search.trim() ? "No bridges match your search." : "No bridges registered."}</p>
                    ) : (
                        <div className="flex flex-col gap-0.5 px-1 py-1">
                            {filtered.map((bridge) => (
                                <BridgeRow key={bridge.id} bridge={bridge} onClick={() => handleBridgeClick(bridge)} />
                            ))}
                        </div>
                    )}
                </div>
            </aside>

            {/* User Profile Modal */}
            {showProfileModal && (
                <ModalOverlay isOpen isDismissable onOpenChange={(open) => !open && setShowProfileModal(false)}>
                    <Modal className="max-w-[960px]">
                        <Dialog>
                            <div className="flex h-[min(90vh,800px)] w-full flex-col overflow-hidden rounded-xl border border-secondary bg-primary shadow-xl">
                                <div className="flex shrink-0 items-center justify-between border-b border-secondary px-6 py-4">
                                    <h2 className="text-lg font-semibold text-primary">Profile</h2>
                                    <Button color="tertiary" size="sm" iconLeading={XClose} onClick={() => setShowProfileModal(false)} aria-label="Close" />
                                </div>
                                <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
                                    <UserProfile
                                        routing="hash"
                                        appearance={{
                                            elements: {
                                                rootBox: "w-full h-full",
                                                cardBox: "w-full h-full shadow-none",
                                                card: "shadow-none w-full h-full",
                                                scrollBox: "overflow-hidden",
                                                navbar: "border-r border-secondary",
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </>
    );
}
